import { getDb } from "../api/firebase-lazy";
import { getFirebaseAuth } from "../api/firebase-ultra-safe";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  runTransaction,
} from "firebase/firestore";
import type { PointTransaction } from "../types";

type FirestoreTimestamp = { toDate?: () => Date } | Date | null | undefined;

const toDateSafe = (timestamp: FirestoreTimestamp, fallback: Date = new Date()): Date => {
  if (!timestamp) {
    return fallback;
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return timestamp.toDate ? timestamp.toDate() : fallback;
};

/**
 * 포인트 잔액 조회
 */
export async function getPointBalance(userId: string): Promise<number> {
  try {
    const db = getDb();
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data().points || 0;
    }
    return 0;
  } catch (error) {
    console.error("포인트 잔액 조회 실패:", error);
    return 0;
  }
}

/**
 * 출금 가능 포인트 잔액 조회 (프로 정산 포인트만)
 */
export async function getWithdrawableBalance(userId: string): Promise<number> {
  try {
    const db = getDb();
    const historyRef = collection(db, "pointHistory");
    
    // orderBy 제거하여 인덱스 오류 방지
    const q = query(
      historyRef,
      where("userId", "==", userId)
    );

    const snapshot = await getDocs(q);
    let withdrawableBalance = 0;

    snapshot.forEach(doc => {
      const data = doc.data();
      
      // 출금 가능한 포인트만 계산 (프로 정산·관리자 지급 등). 회원가입 포인트는 제외
      if (data.withdrawable === true && data.type !== "signup") {
        // 프로 정산으로 받은 포인트 (양수)
        if (data.amount > 0) {
          withdrawableBalance += data.amount;
        }
        // 출금한 포인트 (음수) - withdrawable: true인 출금만 차감
        else if (data.amount < 0) {
          withdrawableBalance += data.amount; // 음수이므로 차감됨
        }
      }
    });

    return Math.max(0, withdrawableBalance);
  } catch (error) {
    console.error("출금 가능 포인트 조회 실패:", error);
    // 인덱스 오류 시 fallback: pro_settlement 타입만 조회
    try {
      const db = getDb();
      const historyRef = collection(db, "pointHistory");
      const fallbackQuery = query(
        historyRef,
        where("userId", "==", userId),
        where("type", "==", "pro_settlement")
      );
      const fallbackSnapshot = await getDocs(fallbackQuery);
      let balance = 0;
      fallbackSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.amount > 0) {
          balance += data.amount;
        }
      });
      return Math.max(0, balance);
    } catch (fallbackError) {
      console.error("출금 가능 포인트 조회 fallback 실패:", fallbackError);
      return 0;
    }
  }
}

/**
 * 포인트 적립/차감 (클라이언트 SDK, 브라우저 전용).
 * API Route·서버에서는 `@/lib/api/points-server`의 addPointsServerSide를 사용합니다.
 */
export async function addPoints(
  userId: string,
  amount: number,
  type: PointTransaction["type"],
  description: string,
  relatedId?: string
): Promise<{ success: boolean; newBalance?: number; error?: string }> {
  try {
    const db = getDb();
    const userRef = doc(db, "users", userId);

    // 중복 지급 방지: relatedId가 있으면 이미 처리된 건인지 확인
    if (relatedId) {
      const historyRef = collection(db, "pointHistory");
      const existing = await getDocs(
        query(
          historyRef,
          where("userId", "==", userId),
          where("relatedId", "==", relatedId)
        )
      );
      if (!existing.empty) {
        const existingData = existing.docs[0].data();
        return {
          success: false,
          error: "이미 처리된 포인트 요청입니다. 중복 지급을 막았습니다.",
          newBalance:
            typeof existingData.balance === "number"
              ? existingData.balance
              : undefined,
        };
      }
    }

    // Firestore 트랜잭션으로 안전하게 처리
    const result = await runTransaction(db, async transaction => {
      const userDoc = await transaction.get(userRef);

      if (!userDoc.exists()) {
        throw new Error("사용자를 찾을 수 없습니다");
      }

      const currentPoints = userDoc.data().points || 0;
      const newBalance = currentPoints + amount;

      // 차감 시 잔액 확인
      if (newBalance < 0) {
        throw new Error("포인트가 부족합니다");
      }

      // 포인트 업데이트
      transaction.update(userRef, {
        points: newBalance,
        updatedAt: new Date(),
      });

      // 포인트 내역 기록
      const pointHistoryRef = collection(db, "pointHistory");
      const historyData: Omit<PointTransaction, "id"> = {
        userId,
        amount,
        type,
        description,
        balance: newBalance,
        createdAt: new Date(),
      };

      if (relatedId) {
        historyData.relatedId = relatedId;
      }

      // 출금 가능 여부 설정
      // 프로 정산·관리자 지급·출금은 출금 가능 잔액에 반영
      if (
        type === "pro_settlement" ||
        type === "admin" ||
        type === "withdrawal"
      ) {
        historyData.withdrawable = true;
      } else {
        // 이벤트, 프로모션, 회원가입 등은 출금 불가
        historyData.withdrawable = false;
      }

      transaction.set(doc(pointHistoryRef), historyData);

      return newBalance;
    });

    console.log(
      `✅ 포인트 ${amount > 0 ? "적립" : "차감"} 완료: ${amount}P, 잔액: ${result}P`
    );
    return { success: true, newBalance: result };
  } catch (error: unknown) {
    console.error("포인트 처리 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "포인트 처리 중 오류가 발생했습니다.",
    };
  }
}

/**
 * 포인트 내역 조회
 */
export async function getPointHistory(
  userId: string,
  options?: { includeHidden?: boolean }
): Promise<PointTransaction[]> {
  try {
    const db = getDb();
    const historyRef = collection(db, "pointHistory");
    const q = query(
      historyRef,
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);
    const history: PointTransaction[] = [];

    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (!options?.includeHidden && data.hiddenFromUser === true) {
        return;
      }
      history.push({
        id: docSnap.id,
        userId: data.userId,
        amount: data.amount,
        type: data.type,
        description: data.description,
        relatedId: data.relatedId,
        balance: data.balance,
        withdrawable: data.withdrawable || false,
        spendRestriction: data.spendRestriction ?? null,
        hiddenFromUser: data.hiddenFromUser === true,
        cancelled: data.cancelled === true,
        cancelReason: data.cancelReason,
        createdAt: toDateSafe(data.createdAt, new Date()),
      });
    });

    return history;
  } catch (error) {
    console.error("포인트 내역 조회 실패:", error);
    return [];
  }
}

/**
 * AI 전용(회원가입 등)으로 남아 있는 포인트
 */
export async function getAiOnlyPointsRemaining(userId: string): Promise<number> {
  try {
    const db = getDb();
    const userSnap = await getDoc(doc(db, "users", userId));
    if (userSnap.exists()) {
      const data = userSnap.data();
      if (typeof data.aiOnlyPointsRemaining === "number") {
        return Math.max(0, Math.min(data.aiOnlyPointsRemaining, data.points || 0));
      }
    }

    const history = await getPointHistory(userId, { includeHidden: true });
    let remaining = 0;
    for (const row of history) {
      if (row.amount <= 0) continue;
      if (row.type === "signup" || row.spendRestriction === "ai_only") {
        remaining += row.amount;
      }
    }
    const total = userSnap.exists() ? userSnap.data()?.points || 0 : remaining;
    return Math.max(0, Math.min(remaining, total));
  } catch (error) {
    console.error("AI 전용 포인트 조회 실패:", error);
    return 0;
  }
}

/**
 * 회원가입 시 웰컴 포인트 지급 (1회만, 트랜잭션으로 중복 방지)
 */
export async function grantSignupPoints(
  userId: string
): Promise<{
  success: boolean;
  newBalance?: number;
  alreadyGranted?: boolean;
  error?: string;
}> {
  const SIGNUP_POINTS = 5000;
  const description = "🎉 회원가입 축하 포인트";
  const signupHistoryId = `${userId}_signup`;

  try {
    const db = getDb();
    const userRef = doc(db, "users", userId);
    const signupHistoryRef = doc(db, "pointHistory", signupHistoryId);

    const existingSignup = await getDoc(signupHistoryRef);
    if (existingSignup.exists()) {
      const balance = await getPointBalance(userId);
      return { success: true, newBalance: balance, alreadyGranted: true };
    }

    const result = await runTransaction(db, async transaction => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error("사용자를 찾을 수 없습니다");
      }

      const signupDoc = await transaction.get(signupHistoryRef);
      if (signupDoc.exists()) {
        const data = userDoc.data();
        return {
          newBalance: data.points || 0,
          alreadyGranted: true as const,
        };
      }

      const data = userDoc.data();
      if (data.signupBonusGranted) {
        return {
          newBalance: data.points || 0,
          alreadyGranted: true as const,
        };
      }

      const currentPoints = data.points || 0;
      const newBalance = currentPoints + SIGNUP_POINTS;

      const historyData: Omit<PointTransaction, "id"> = {
        userId,
        amount: SIGNUP_POINTS,
        type: "signup",
        description,
        balance: newBalance,
        createdAt: new Date(),
        withdrawable: false,
        spendRestriction: "ai_only",
      };
      transaction.set(signupHistoryRef, historyData);

      transaction.update(userRef, {
        points: newBalance,
        signupBonusGranted: true,
        aiOnlyPointsRemaining:
          Number(data.aiOnlyPointsRemaining || 0) + SIGNUP_POINTS,
        updatedAt: new Date(),
      });

      return { newBalance, alreadyGranted: false as const };
    });

    if (result.alreadyGranted) {
      console.log(`⚠️ 회원가입 포인트 이미 지급됨 (userId: ${userId})`);
    } else {
      console.log(
        `✅ 회원가입 포인트 지급 완료: ${SIGNUP_POINTS}P, 잔액: ${result.newBalance}P`
      );
    }

    return { success: true, ...result };
  } catch (error: unknown) {
    console.error("회원가입 포인트 지급 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "회원가입 포인트 지급 중 오류가 발생했습니다.",
    };
  }
}

/**
 * 거래 완료 시 포인트 지급
 */
export async function grantTradeCompletePoints(
  userId: string,
  itemId: string
): Promise<{ success: boolean; error?: string }> {
  const TRADE_POINTS = 100;

  return await addPoints(
    userId,
    TRADE_POINTS,
    "trade_complete",
    "✅ 거래 완료 포인트",
    itemId
  );
}

/**
 * 후기 작성 시 포인트 지급
 * 이미 해당 아이템에 대한 후기 포인트를 받았는지 확인
 */
export async function grantReviewPoints(
  userId: string,
  itemId: string
): Promise<{ success: boolean; error?: string }> {
  const REVIEW_POINTS = 50;

  try {
    const db = getDb();

    // 이미 해당 아이템에 대한 후기 포인트를 받았는지 확인
    const historyRef = collection(db, "pointHistory");
    const q = query(
      historyRef,
      where("userId", "==", userId),
      where("type", "==", "review"),
      where("relatedId", "==", itemId)
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      console.log(
        `⚠️ 이미 ${itemId} 아이템에 대한 후기 포인트를 받았습니다. 중복 지급 방지.`
      );
      return {
        success: false,
        error: "이미 해당 거래에 대한 후기 포인트를 받았습니다.",
      };
    }

    // 중복이 없으면 포인트 지급
    return await addPoints(
      userId,
      REVIEW_POINTS,
      "review",
      "⭐ 후기 작성 포인트",
      itemId
    );
  } catch (error: unknown) {
    console.error("후기 포인트 지급 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "후기 포인트 지급 중 오류가 발생했습니다.",
    };
  }
}

/**
 * 포인트로 결제 (심화 분석 등) — 서버 API 경유 (클라이언트는 users.points 직접 수정 불가)
 */
export async function spendPoints(
  userId: string,
  amount: number,
  description: string,
  relatedId?: string,
  purpose: "ai" | "general" = "ai"
): Promise<{ success: boolean; newBalance?: number; error?: string }> {
  try {
    const auth = getFirebaseAuth();
    const currentUid = auth?.currentUser?.uid;
    if (!currentUid || currentUid !== userId) {
      return { success: false, error: "로그인이 필요합니다." };
    }

    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) {
      return { success: false, error: "로그인이 필요합니다." };
    }

    const res = await fetch("/api/points/spend", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ amount, description, relatedId, purpose }),
    });

    const json = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      newBalance?: number;
      error?: string;
    };

    if (!res.ok || !json.success) {
      return {
        success: false,
        error: json.error || "포인트 차감에 실패했습니다.",
      };
    }

    return { success: true, newBalance: json.newBalance };
  } catch (error: unknown) {
    console.error("포인트 차감 요청 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "포인트 차감 중 오류가 발생했습니다.",
    };
  }
}

/**
 * 이벤트 포인트 지급 (출금 불가)
 */
export async function grantEventPoints(
  userId: string,
  amount: number,
  description: string,
  relatedId?: string
): Promise<{ success: boolean; newBalance?: number; error?: string }> {
  return await addPoints(userId, amount, "event", description, relatedId);
}

/**
 * 프로모션 포인트 지급 (출금 불가)
 */
export async function grantPromotionPoints(
  userId: string,
  amount: number,
  description: string,
  relatedId?: string
): Promise<{ success: boolean; newBalance?: number; error?: string }> {
  return await addPoints(userId, amount, "promotion", description, relatedId);
}

/**
 * 프로 정산 포인트 지급 (출금 가능)
 * 중복 지급 방지: relatedId가 있으면 이미 지급되었는지 확인
 */
export async function grantProSettlementPoints(
  userId: string,
  amount: number,
  description: string,
  relatedId?: string
): Promise<{ success: boolean; newBalance?: number; settlementId?: string; error?: string }> {
  try {
    const db = getDb();

    // 중복 지급 방지: relatedId가 있으면 이미 지급되었는지 확인
    if (relatedId) {
      const historyRef = collection(db, "pointHistory");
      const q = query(
        historyRef,
        where("userId", "==", userId),
        where("type", "==", "pro_settlement"),
        where("relatedId", "==", relatedId)
      );

      const existingSnapshot = await getDocs(q);
      if (!existingSnapshot.empty) {
        console.log(
          `⚠️ 이미 ${relatedId}에 대한 프로 정산 포인트가 지급되었습니다. 중복 지급 방지.`
        );
        const existingDoc = existingSnapshot.docs[0];
        const existingData = existingDoc.data();
        return {
          success: false,
          error: "이미 해당 피드백에 대한 정산이 완료되었습니다.",
          settlementId: existingDoc.id,
        };
      }
    }

    const userRef = doc(db, "users", userId);

    // Firestore 트랜잭션으로 안전하게 처리
    const result = await runTransaction(db, async transaction => {
      const userDoc = await transaction.get(userRef);

      if (!userDoc.exists()) {
        throw new Error("사용자를 찾을 수 없습니다");
      }

      const currentPoints = userDoc.data().points || 0;
      const newBalance = currentPoints + amount;

      // 포인트 업데이트
      transaction.update(userRef, {
        points: newBalance,
        updatedAt: new Date(),
      });

      // 포인트 내역 기록 (출금 가능으로 설정)
      const pointHistoryRef = collection(db, "pointHistory");
      const historyDocRef = doc(pointHistoryRef);
      const historyData: Omit<PointTransaction, "id"> = {
        userId,
        amount,
        type: "pro_settlement",
        description,
        balance: newBalance,
        withdrawable: true, // 출금 가능
        createdAt: new Date(),
      };

      if (relatedId) {
        historyData.relatedId = relatedId;
      }

      transaction.set(historyDocRef, historyData);

      return { newBalance, settlementId: historyDocRef.id };
    });

    console.log(
      `✅ 프로 정산 포인트 지급 완료: ${amount}P, 잔액: ${result.newBalance}P (출금 가능)`
    );
    return {
      success: true,
      newBalance: result.newBalance,
      settlementId: result.settlementId,
    };
  } catch (error: unknown) {
    console.error("프로 정산 포인트 지급 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "프로 정산 포인트 지급 중 오류가 발생했습니다.",
    };
  }
}

/**
 * 포인트 출금 (출금 가능 포인트만 차감)
 */
export async function withdrawPoints(
  userId: string,
  amount: number,
  description: string
): Promise<{ success: boolean; newBalance?: number; error?: string }> {
  try {
    // 출금 가능 포인트 확인
    const withdrawableBalance = await getWithdrawableBalance(userId);
    if (amount > withdrawableBalance) {
      return {
        success: false,
        error: "출금 가능한 포인트가 부족합니다.",
      };
    }

    const db = getDb();
    const userRef = doc(db, "users", userId);

    // Firestore 트랜잭션으로 안전하게 처리
    const result = await runTransaction(db, async transaction => {
      const userDoc = await transaction.get(userRef);

      if (!userDoc.exists()) {
        throw new Error("사용자를 찾을 수 없습니다");
      }

      const currentPoints = userDoc.data().points || 0;
      const newBalance = currentPoints - amount;

      // 차감 시 잔액 확인
      if (newBalance < 0) {
        throw new Error("포인트가 부족합니다");
      }

      // 포인트 업데이트
      transaction.update(userRef, {
        points: newBalance,
        updatedAt: new Date(),
      });

      // 포인트 내역 기록 (출금 가능 포인트 차감)
      const pointHistoryRef = collection(db, "pointHistory");
      const historyData: Omit<PointTransaction, "id"> = {
        userId,
        amount: -amount, // 음수로 기록
        type: "withdrawal",
        description,
        balance: newBalance,
        withdrawable: true, // 출금 가능 포인트에서 차감
        createdAt: new Date(),
      };

      transaction.set(doc(pointHistoryRef), historyData);

      return newBalance;
    });

    console.log(
      `✅ 포인트 출금 완료: ${amount}P, 잔액: ${result}P`
    );
    return { success: true, newBalance: result };
  } catch (error: unknown) {
    console.error("포인트 출금 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "포인트 출금 중 오류가 발생했습니다.",
    };
  }
}
